import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  InputLabel,
} from '@material-ui/core';
import { EnvironmentDTO } from '../../DTO/Environment';
import { MarketplaceMetricsDTO } from '../../DTO/MarketplaceMetrics';
import * as Api from '../../services';
import MetricsDisplay from './MetricsDisplay';


interface DashboardCardProps {
  token: string;
}

const environmentSelectMenuProps = {
  MenuListProps: {
    style: {
      display: 'block',
    },
  },
  PaperProps: {
    style: {
      minWidth: 180,
    },
  },
};

function normalizeMetrics(data: Partial<MarketplaceMetricsDTO>): MarketplaceMetricsDTO {
  return {
    totalProjects: data.totalProjects ?? 0,
    coverage: {
      total: data.coverage?.total ?? 0,
      withCoverage: data.coverage?.withCoverage ?? 0,
      averageCoverage: data.coverage?.averageCoverage ?? null,
      withCoverageThreshold: data.coverage?.withCoverageThreshold ?? 0,
      aboveThreshold: data.coverage?.aboveThreshold ?? 0,
      belowThreshold: data.coverage?.belowThreshold ?? 0,
    },
    security: {
      total: data.security?.total ?? 0,
      withSastReport: data.security?.withSastReport ?? 0,
      withSecretDetection: data.security?.withSecretDetection ?? 0,
      averageSastWarnings: data.security?.averageSastWarnings ?? null,
      averageSecretDetectionWarnings: data.security?.averageSecretDetectionWarnings ?? null,
    },
    dependencies: {
      total: data.dependencies?.total ?? 0,
      critical: data.dependencies?.critical ?? 0,
      high: data.dependencies?.high ?? 0,
      medium: data.dependencies?.medium ?? 0,
      low: data.dependencies?.low ?? 0,
      averageCritical: data.dependencies?.averageCritical ?? null,
      averageHigh: data.dependencies?.averageHigh ?? null,
      averageMedium: data.dependencies?.averageMedium ?? null,
      averageLow: data.dependencies?.averageLow ?? null,
    },
    agenticEvaluator: {
      withDocumentationScore: data.agenticEvaluator?.withDocumentationScore ?? 0,
      withArchitecturalFitness: data.agenticEvaluator?.withArchitecturalFitness ?? 0,
      withWeakPointCounters: data.agenticEvaluator?.withWeakPointCounters ?? 0,
      averageDocumentationScore: data.agenticEvaluator?.averageDocumentationScore ?? null,
      averageArchitecturalFitness: data.agenticEvaluator?.averageArchitecturalFitness ?? null,
      weakPointCounters: Array.isArray(data.agenticEvaluator?.weakPointCounters)
        ? data.agenticEvaluator?.weakPointCounters
        : [],
      weakPointCategories: Array.isArray(data.agenticEvaluator?.weakPointCategories)
        ? data.agenticEvaluator?.weakPointCategories
        : [],
    },
    packageCompliance: {
      total: data.packageCompliance?.total ?? 0,
      low: data.packageCompliance?.low ?? 0,
      medium: data.packageCompliance?.medium ?? 0,
      high: data.packageCompliance?.high ?? 0,
      averageLow: data.packageCompliance?.averageLow ?? null,
      averageMedium: data.packageCompliance?.averageMedium ?? null,
      averageHigh: data.packageCompliance?.averageHigh ?? null,
    },
  };
}


const DashboardCard: React.FC<DashboardCardProps> = React.memo(({ token }) => {
  const [environments, setEnvironments] = useState<EnvironmentDTO[]>([]);
  const [selectedEnv, setSelectedEnv] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<MarketplaceMetricsDTO | null>(null);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [metricsLoading, setMetricsLoading] = useState<boolean>(false);

  const loadMetrics = async (environment: string) => {
    setSelectedEnv(environment);
    setMetrics(null);
    setMetricsError(null);
    setMetricsLoading(true);

    try {
      const data = await Api.apiGet<MarketplaceMetricsDTO>(
        `/api/marketplace-metrics?environment=${encodeURIComponent(environment)}`,
        token
      );
      setMetrics(normalizeMetrics(data));
    } catch (err: any) {
      setMetricsError(err?.message || 'Failed to load marketplace metrics');
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  };

  useEffect(() => {
    setMetrics(null);
    setMetricsError(null);

    if (!token) {
      setEnvironments([]);
      setSelectedEnv('');
      return;
    }

    let mounted = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const data = await Api.apiGet<EnvironmentDTO[]>('/api/environments', token);
        if (!mounted) return;
        if (Array.isArray(data)) {
          setEnvironments(data);
          if (data.length > 0) {
            await loadMetrics(data[0].name);
          } else {
            setSelectedEnv('');
          }
        } else {
          setEnvironments([]);
        }
      } catch (err: any) {
        if (!mounted) return;
        setError(err?.message || 'Failed to load environments');
        setEnvironments([]);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [token]);

  const handleEnvChange = async (event: any) => {
    const environment = event.target.value as string;
    await loadMetrics(environment);
  };

  return (
    <Paper style={{ paddingLeft: 24 , paddingBottom: 24, paddingTop: 8, paddingRight: 24 }}>
       <Box display="flex" alignItems="flex-start" justifyContent="space-between" width="100%">
        <Box flex="1 1 auto" minWidth={0} mr={2}>
          {selectedEnv && (
            <>
              <Typography variant="h5" gutterBottom>
                General
              </Typography>
              <Typography variant="body1" gutterBottom>
                This dashboard provides a summary of the computed quality metrics of the projects in the selected environment.
              </Typography>
            </>
          )}
        </Box>
        <Box flex="0 0 auto" display="flex" alignItems="center" justifyContent="flex-end">
          <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
            <InputLabel id="env-select">Environment</InputLabel>
            <Select
              labelId="env-select"
              value={selectedEnv}
              onChange={handleEnvChange}
              label="Environment"
              disabled={loading || !!error || environments.length === 0}              
              MenuProps={environmentSelectMenuProps}
            >
              {!loading && environments.map((env) => (
                <MenuItem key={env.name} value={env.name} style={{ display: 'flex' }}>
                  {env.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {loading && <CircularProgress size={20} style={{ marginLeft: 12 }} />}
          {error && (
            <Typography variant="caption" color="error" style={{ marginLeft: 12 }}>
              Env load error
            </Typography>
          )}
        </Box>
      </Box>
    
      <Box mt={2} display="flex" justifyContent="center">
        <Box width="100%" maxWidth={1200}>
          <Box mt={2}>
            {metricsLoading && (
              <Box display="flex" justifyContent="center" mt={2}>
                <CircularProgress size={28} />
              </Box>
            )}
            {metricsError && (
              <Box mt={2}>
                <Typography variant="body2" color="error">{metricsError}</Typography>
              </Box>
            )}
            {!metricsLoading && metrics === null && !metricsError && (
              <Box mt={4} display="flex" justifyContent="center">
                <Typography variant="body1" color="textSecondary">
                  Select an environment to view the metrics.
                </Typography>
              </Box>
            )}
            {metrics !== null && !metricsLoading && (
              <MetricsDisplay metrics={metrics} />
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
});

export default DashboardCard;
